import { useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import PhotoPicker from "@/components/PhotoPicker";

/**
 * Re-usable uploader for order completion photos. Uses <PhotoPicker> so
 * operators can either take a fresh photo or choose from the device gallery.
 */
export default function OrderPhotosUploader({ orderId, photos = [], onChange, canRemovePhoto }) {
  const [busy, setBusy] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    const next = [...photos];
    let added = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        // Don't set Content-Type explicitly — axios + FormData auto-generates the multipart boundary.
        const up = await api.post("/upload", fd);
        const photo = await api.post(`/orders/${orderId}/photos`, { file_id: up.data.id, caption: "" });
        next.push(photo.data);
        added += 1;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Upload failed for ${f.name}`);
      }
    }
    if (added) {
      onChange?.(next);
      toast.success(`${added} photo${added === 1 ? "" : "s"} attached`);
    }
    setBusy(false);
  };

  const removePhoto = async (photoId) => {
    setRemovingId(photoId);
    try {
      await api.delete(`/orders/${orderId}/photos/${photoId}`);
      onChange?.(photos.filter((p) => p.id !== photoId));
      toast.success("Photo removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="rounded-2xl border border-cyan-100 bg-white/60 p-3" data-testid={`order-photos-uploader-${orderId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium text-slate-800">
          <Camera className="mr-1 inline h-4 w-4 text-cyan-700" />
          Completion photos
          <span className="ml-2 text-xs text-slate-500">{photos.length}</span>
        </div>
        <PhotoPicker onFiles={onFiles} busy={busy} size="sm" testIdPrefix={`order-photos-${orderId}`} />
      </div>

      {photos.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">No photos yet. Take a new photo or pick from your device.</p>
      ) : (
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
          {photos.map((p) => {
            const allowRemove = canRemovePhoto ? canRemovePhoto(p) : true;
            return (
              <div key={p.id} className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                <img src={p.file_id ? fileUrl(p.file_id) : p.url} alt={p.caption || "Completion"} className="h-full w-full object-cover" loading="lazy" />
                {p.uploaded_by_name && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-black/60 px-1.5 py-1 text-[10px] text-white">
                    {p.uploaded_by_name}
                  </div>
                )}
                {allowRemove && (
                  <button
                    type="button"
                    onClick={() => removePhoto(p.id)}
                    disabled={removingId === p.id}
                    title="Remove photo"
                    className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/70 text-white opacity-0 transition group-hover:opacity-100 focus:opacity-100"
                    data-testid={`order-photo-remove-${p.id}`}
                  >
                    {removingId === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
